import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import $ from 'jquery';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as action from '../Action/Categories/Categories_Action';
import Model from "./Model/Index";
import NumberDisplay from './NumberDisplay/Index';
import { usePermission } from "../hooks/usePermission";
import { PERM } from "../config/permissions";
import { auditDataTableColumns, AuditTrailStyles } from "./AuditTrail";

const Genres = () => {
    const { canCreate, canEdit, canDelete } = usePermission();
    const allowCreate = canCreate(PERM.CATEGORIES);
    const allowEdit = canEdit(PERM.CATEGORIES);
    const allowDelete = canDelete(PERM.CATEGORIES);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const updateCategoriesLoading = useSelector(state => state.UpdateCategoriesReducer);
    const addCategoriesLoading = useSelector(state => state.AddCategoriesReducer);
    const [showModel, setShowModel] = useState({ show: false, title: 'Add' });
    const [count, setCount] = useState(0);
    const [errors, setErrors] = useState({});
    const [categoryData, setCategoryData] = useState({ id: 0, name: '' });
    const dataTableInstanceRef = useRef(null);
    const nameRefFocus = useRef(null);
    const isSubmittingRef = useRef(false);

    const [rankingMode, setRankingMode] = useState(false);
    const [rankItems, setRankItems] = useState([]);
    const [rankLoading, setRankLoading] = useState(false);
    const [savingRank, setSavingRank] = useState(false);

    const dragSrcIdxRef = useRef(null);

    const fetchAllForRanking = useCallback(() => {
        setRankLoading(true);
        dispatch(action.getCategories({ page: 1, limit: 1000 }))
            .then((res) => {
                const rd = res.responseDetails || {};
                const rows = rd.rows || rd.data || [];
                const sorted = [...rows].sort((a, b) => {
                    if (a.rank == null && b.rank == null) return a.id - b.id;
                    if (a.rank == null) return 1;
                    if (b.rank == null) return -1;
                    return a.rank - b.rank;
                });
                setRankItems(sorted);
            })
            .catch(() => toast.error('Failed to load categories for ranking.'))
            .finally(() => setRankLoading(false));
    }, [dispatch]);

    const enterRankingMode = () => {
        if (dataTableInstanceRef.current) {
            try { dataTableInstanceRef.current.destroy(); } catch (e) {}
            dataTableInstanceRef.current = null;
        }
        setRankingMode(true);
        fetchAllForRanking();
    };

    const exitRankingMode = useCallback(() => {
        setRankingMode(false);
        setRankItems([]);
    }, []);

    const saveRanking = () => {
        // Reordering writes categories, so it is gated on the same Edit
        // permission the backend enforces on /categories/update-rank.
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        setSavingRank(true);
        const ranks = rankItems.map((item, index) => ({ id: item.id, rank: index + 1 }));
        dispatch(action.updateCategoryRanks({ ranks }))
            .then(() => {
                toast.success('Category order saved successfully!');
                exitRankingMode();
            })
            .catch((err) => {
                toast.error(err?.responseMessage || err?.message || 'Failed to save order. Please try again.');
            })
            .finally(() => setSavingRank(false));
    };

    const onDragStart = (e, index) => {
        dragSrcIdxRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    };

    const onDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const src = dragSrcIdxRef.current;
        if (src === null || src === index) return;
        setRankItems(prev => {
            const next = [...prev];
            const [removed] = next.splice(src, 1);
            next.splice(index, 0, removed);
            dragSrcIdxRef.current = index;
            return next;
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
    };

    useEffect(() => {
        if (rankingMode) return;

        let tableRefCurrent = tableRef.current;
        const $ = window.$ || window.jQuery;
        let currentSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';

        function setPaginationOptions() {
            if (currentSize === 'mobile') {
                $.fn.dataTable.ext.pager.numbers_length = 0;
                return 'simple';
            } else {
                $.fn.dataTable.ext.pager.numbers_length = 5;
                return 'simple_numbers';
            }
        }

        const initDataTable = () => {
            const pagingType = setPaginationOptions();
            if ($ && tableRefCurrent) {
                dataTableInstanceRef.current = $(tableRefCurrent).DataTable({
                    processing: true,
                    serverSide: true,
                    scrollX: true,
                    pagingType: pagingType,
                    ajax: (dataTablesParams, callback) => {
                        const params = {
                            search: dataTablesParams.search.value,
                            page: Math.floor(dataTablesParams.start / dataTablesParams.length) + 1,
                            limit: dataTablesParams.length,
                        };
                        dispatch(action.getCategories(params)).then((res) => {
                            const rd = res.responseDetails || {};
                            const rows = rd.rows || rd.data || [];
                            const total = rd.total || rd.totalRecords || 0;
                            setCount(total);
                            callback({
                                draw: dataTablesParams.draw,
                                data: rows,
                                recordsTotal: total,
                                recordsFiltered: rd.recordsFiltered || total,
                            });
                        }).catch((err) => {
                            console.error('Genres list error:', err);
                            callback({ draw: dataTablesParams.draw, data: [], recordsTotal: 0, recordsFiltered: 0 });
                        });
                    },
                    destroy: true,
                    columns: [
                        { data: 'name', title: 'Title' },
                        ...auditDataTableColumns(),
                        {
                            data: null,
                            title: 'Action',
                            orderable: false,
                            render: function () {
                                return `<div class="d-flex gap-2">
                                    <button class="btn btn-sm upgrade-btn view-btn" title="View Series"><i class="fas fa-eye"></i></button>
                                    ${allowEdit ? '<button class="btn btn-sm btn-primary edit-btn" title="Edit"><i class="fas fa-edit"></i></button>' : ''}
                                    ${allowDelete ? '<button class="btn btn-sm btn-danger delete-btn" title="Delete"><i class="fas fa-trash-alt"></i></button>' : ''}
                                </div>`;
                            }
                        }
                    ]
                });

                $(tableRefCurrent).on('preXhr.dt', function () {
                    const processingEl = $('.dataTables_processing');
                    const visibleCols = $(tableRefCurrent).find('thead th:visible').length;
                    if (processingEl.length) {
                        $(tableRefCurrent).find('tbody').html(
                            `<tr class="dt-temp-loader"><td colspan="${visibleCols}" class="text-center"><div class="dataTables_processing card" style="display:block;margin:0 auto">${processingEl.html()}</div></td></tr>`
                        );
                    }
                });

                $(tableRefCurrent).on('xhr.dt', function () {
                    $(tableRefCurrent).find('tbody .dt-temp-loader').remove();
                });

                $(tableRefCurrent).off('click', '.view-btn');
                $(tableRefCurrent).on('click', '.view-btn', function () {
                    const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    navigate(`/genres/${rowData.id}/series`, { state: { categoryName: rowData.name } });
                });

                $(tableRefCurrent).off('click', '.edit-btn');
                $(tableRefCurrent).on('click', '.edit-btn', function () {
                    const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    openModel('Edit');
                    setCategoryData({ id: rowData.id, name: rowData.name });
                });

                $(tableRefCurrent).off('click', '.delete-btn');
                $(tableRefCurrent).on('click', '.delete-btn', function () {
                    if (!allowDelete) {
                        toast.error("Opps! You don't have Permission.");
                        return;
                    }
                    const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    Swal.fire({
                        title: 'Are you sure?',
                        text: "You won't be able to revert this!",
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: 'var(--first-color)',
                        cancelButtonColor: 'var(--error-color)',
                        confirmButtonText: 'Yes, delete it!',
                        customClass: { confirmButton: 'btn', cancelButton: 'btn' }
                    }).then((result) => {
                        if (result.isConfirmed) {
                            dispatch(action.deleteCategories({ id: rowData.id }))
                                .then(() => {
                                    Swal.fire({ title: 'Deleted!', text: 'Your data has been deleted.', icon: 'success', customClass: { confirmButton: 'btn' } });
                                    dataTableInstanceRef.current?.ajax.reload(null, false);
                                })
                                .catch(() => {
                                    Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', customClass: { confirmButton: 'btn' } });
                                });
                        }
                    });
                });
            }
        };

        initDataTable();

        const handleResize = () => {
            const newSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';
            if (newSize !== currentSize) {
                currentSize = newSize;
                if (dataTableInstanceRef.current) {
                    try { dataTableInstanceRef.current.destroy(); } catch (e) {}
                    dataTableInstanceRef.current = null;
                }
                initDataTable();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (dataTableInstanceRef.current) {
                try { dataTableInstanceRef.current.destroy(); } catch (e) {}
                dataTableInstanceRef.current = null;
            }
        };
    }, [dispatch, rankingMode, allowEdit, allowDelete]);

    const openModel = (name) => {
        setShowModel({ show: true, title: name });
        $('body').addClass('no_scroll');
        $('.overlay').addClass('qv_active');
    };

    const closeModel = () => {
        isSubmittingRef.current = false;
        setShowModel({ show: false, title: 'Add' });
        $('body').removeClass('no_scroll');
        $('.overlay').removeClass('qv_active');
        setErrors({});
        setCategoryData({ id: 0, name: '' });
    };

    const onChange = (e) => {
        setCategoryData({ ...categoryData, [e.target.name]: e.target.value });
    };

    const addCategoryData = (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) return;

        // Covers both Add and Edit — the backend guards /categories/add and
        // /categories/update with the same categories:edit permission.
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        setErrors({});
        if (categoryData.name === '') {
            setErrors({ name: 'Please Enter name' });
            nameRefFocus.current?.focus();
            return;
        }

        isSubmittingRef.current = true;
        const data = { name: categoryData.name };
        if (categoryData.id !== 0) {
            data.id = categoryData.id;
            dispatch(action.updateCategories(data))
                .then((res) => {
                    isSubmittingRef.current = false;
                    toast.success(res.responseMessage);
                    closeModel();
                    dataTableInstanceRef.current?.ajax.reload(null, false);
                })
                .catch(err => {
                    isSubmittingRef.current = false;
                    toast.error(err?.responseMessage || err?.message || 'An error occurred.');
                });
        } else {
            dispatch(action.addCategories(data))
                .then((res) => {
                    isSubmittingRef.current = false;
                    toast.success(res.responseMessage);
                    closeModel();
                    dataTableInstanceRef.current?.ajax.reload(null, false);
                })
                .catch(err => {
                    isSubmittingRef.current = false;
                    toast.error(err?.responseMessage || err?.message || 'An error occurred.');
                });
        }
    };

    return (
        <>
            <AuditTrailStyles />
            <div className='overlay'></div>
            <section id="genres-section" className="genres-section section">
                <div className="card">
                    <div className="card-header">
                        <div className="page-title w-100 d-flex align-items-center justify-content-between">
                            <h4>Categories (<span className="total_user"><NumberDisplay count={count} /></span>)</h4>
                            <div className="d-flex gap-2">
                                {!rankingMode ? (
                                    <>
                                        {allowEdit && (
                                            <button
                                                className="btn"
                                                onClick={enterRankingMode}
                                                style={{ border: '1px solid var(--first-color)', color: 'var(--first-color)', background: 'transparent', fontWeight: 500 }}
                                            >
                                                <i className="fas fa-sort" style={{ marginRight: '6px' }}></i>
                                                Manage Order
                                            </button>
                                        )}
                                        {allowCreate && (
                                            <button className="btn upgrade-btn add-genre-btn" onClick={() => openModel('Add')}>
                                                Add Categories
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <button className="btn btn-secondary" onClick={exitRankingMode} disabled={savingRank}>
                                            Cancel
                                        </button>
                                        <button className="btn upgrade-btn" onClick={saveRanking} disabled={savingRank || rankLoading}>
                                            {savingRank
                                                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 14, height: 14 }} /> Saving...</>
                                                : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>Save Order</>
                                            }
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="card-body">
                        {rankingMode ? (
                            <>
                                <p style={{ color: '#888', fontSize: '13px', marginBottom: '12px' }}>
                                    <i className="fas fa-info-circle" style={{ marginRight: '6px', color: 'var(--first-color)' }}></i>
                                    Drag rows using the <strong>⠿</strong> handle to reorder. Click <strong>Save Order</strong> when done.
                                </p>
                                {rankLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status" />
                                    </div>
                                ) : (
                                    <table className="table table-striped w-100" style={{ tableLayout: 'fixed' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 40 }}></th>
                                                <th style={{ width: 50 }}>#</th>
                                                <th>Title</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rankItems.map((item, index) => (
                                                <tr
                                                    key={item.id}
                                                    draggable
                                                    onDragStart={(e) => onDragStart(e, index)}
                                                    onDragEnd={onDragEnd}
                                                    onDragOver={(e) => onDragOver(e, index)}
                                                    onDrop={onDrop}
                                                    style={{ cursor: 'default' }}
                                                >
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span
                                                            style={{ cursor: 'grab', fontSize: '18px', color: 'var(--first-color, #e91e63)', userSelect: 'none' }}
                                                            title="Drag to reorder"
                                                        >
                                                            ⠿
                                                        </span>
                                                    </td>
                                                    <td style={{ color: '#888', fontSize: '13px' }}>{index + 1}</td>
                                                    <td>{item.name}</td>
                                                </tr>
                                            ))}
                                            {rankItems.length === 0 && (
                                                <tr>
                                                    <td colSpan={3} className="text-center py-4" style={{ color: '#888' }}>No categories found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        ) : (
                            <table
                                ref={tableRef}
                                className="genres-table user-table table table-striped w-100 dataTable no-footer"
                                id="genres-Contents"
                                role="grid"
                            />
                        )}
                    </div>
                </div>
            </section>

            {showModel.show && (
                <Model className="common-popup add-genre-popup active">
                    <div className="edit-heading d-flex align-items-center justify-content-between">
                        <h3>{showModel.title} Category</h3>
                        <button className="common-close edit-close-btn" onClick={closeModel}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18">
                                <path fill="#ffffff" d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z" />
                            </svg>
                        </button>
                    </div>
                    <div className="edit-inner form-img-upload">
                        <form onSubmit={addCategoryData}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="name">Name</label>
                                        <input type="text" id="name" name="name" value={categoryData.name} onChange={onChange} placeholder="Enter name" ref={nameRefFocus} />
                                        <span className='text-danger pt-2'>{errors?.name}</span>
                                    </div>
                                    <div className="action-footer">
                                        <button type="submit" className="btn" disabled={showModel.title === 'Add' ? addCategoriesLoading.loading : updateCategoriesLoading.loading}>
                                            {(showModel.title === 'Add' ? addCategoriesLoading.loading : updateCategoriesLoading.loading)
                                                ? <div className="spinner-border text-light" style={{ width: 16, height: 16 }} role="status"><span className="visually-hidden">Loading...</span></div>
                                                : 'Save'
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </Model>
            )}
        </>
    );
};

export default Genres;
